import { useCallback, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import toast from 'react-hot-toast';
import {
  AcceptGameInviteResponse,
  getGameInviteAcceptApi,
  getGameInviteDeclineApi,
} from 'constants/payloads/connections';
import { useGetMyGameInvitesQuery } from 'types/generated/client';
import { useApiGateway } from 'hooks/useApi';
import { useViewer } from 'hooks/useViewer';

/**
 * The game invites waiting on the viewer, and the two answers to them.
 *
 * One query for all of them rather than one per game: the same cached list backs
 * the invite footer inside a game's detail modal and any list of pending invites,
 * and both refresh together after an answer.
 */
export const useMyGameInvites = () => {
  const { userId } = useViewer();
  const apolloClient = useApolloClient();
  const { post } = useApiGateway<undefined, AcceptGameInviteResponse>();
  const [pendingInviteId, setPendingInviteId] = useState<string | null>(null);

  // Held in state so a long-open modal doesn't keep re-running the query as `now`
  // drifts; the list refetches explicitly after each answer.
  const [now] = useState(() => new Date().toISOString());

  const { data, loading, refetch } = useGetMyGameInvitesQuery({
    variables: { userId, now },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  const invites = useMemo(() => data?.eventPlayerInvites ?? [], [data]);

  const answer = useCallback(
    async (inviteId: string, endpoint: string, errorMessage: string, successMessage?: string) => {
      setPendingInviteId(inviteId);
      // An answer moves the viewer's seat and the game's roster, and a failed one
      // usually means the invite or game changed underneath, so both paths refresh.
      const refreshAll = () =>
        Promise.all([
          refetch(),
          apolloClient.refetchQueries({
            include: ['GetMyGameRequest', 'GetHostGame', 'GetFindGamesWithRoster'],
          }),
        ]);
      try {
        const response = await post({ endpoint });

        if (response.isError) {
          toast.error(response.error || errorMessage);
          await refreshAll();
          return false;
        }

        if (successMessage) {
          toast.success(successMessage);
        }

        await refreshAll();
        return true;
      } finally {
        setPendingInviteId(null);
      }
    },
    [post, refetch, apolloClient],
  );

  const acceptInvite = useCallback(
    (inviteId: string) =>
      answer(
        inviteId,
        getGameInviteAcceptApi(inviteId),
        'Could not accept this invite',
        "You're in — see you on court",
      ),
    [answer],
  );

  const declineInvite = useCallback(
    (inviteId: string) =>
      answer(inviteId, getGameInviteDeclineApi(inviteId), 'Could not decline this invite'),
    [answer],
  );

  /** The viewer's pending invite to one game, if they have one. */
  const getInviteForEvent = useCallback(
    (eventId: string | null | undefined) =>
      eventId ? invites.find((invite) => invite.eventId === eventId) ?? null : null,
    [invites],
  );

  return {
    invites,
    isLoading: loading,
    pendingInviteId,
    isSubmitting: Boolean(pendingInviteId),
    acceptInvite,
    declineInvite,
    getInviteForEvent,
    refetch,
  };
};
